import type { TFunction } from "i18next";
import type {
  EventOrderArrivalEntry,
  EventSessionOrder,
} from "../useCalendarView";

export const ARRIVAL_ACTION_ARRIVED = "ARRIVED";

export type AttendanceTarget = {
  key: string;
  guestIndex: number;
  optionIndex?: number;
  optionLabel?: string;
};

export type SetOrderArrivalInput = {
  orderId: string;
  sessionIds: string[];
  attendanceKeys: string[];
  arrived: boolean;
  note?: string;
};

export type SetOrderArrival = (input: SetOrderArrivalInput) => Promise<void>;

export function getGuestAttendanceKey(guestIndex: number): string {
  return `guest:${guestIndex}`;
}

export function getEventOptionAttendanceKey(
  sourceTransactionId: string | null | undefined,
  guestIndex: number,
  optionIndex: number,
): string {
  const stableSourceTransactionId = sourceTransactionId?.trim();
  return stableSourceTransactionId
    ? `event-option:${stableSourceTransactionId}`
    : `guest:${guestIndex}:option:${optionIndex}`;
}

/** Builds one independently checkable target per guest or guest event option. */
export function deriveAttendanceTargets(
  order: Pick<EventSessionOrder, "quantity" | "guests">,
): AttendanceTarget[] {
  const sourceTransactionIdCounts = new Map<string, number>();
  for (const guest of order.guests ?? []) {
    for (const option of guest?.eventOptions ?? []) {
      const sourceTransactionId = option?.sourceTransactionId?.trim();
      if (sourceTransactionId) {
        sourceTransactionIdCounts.set(
          sourceTransactionId,
          (sourceTransactionIdCounts.get(sourceTransactionId) ?? 0) + 1,
        );
      }
    }
  }

  return Array.from({ length: order.quantity }, (_, guestIndex) => {
    const eventOptions = (
      order.guests?.[guestIndex]?.eventOptions ?? []
    ).flatMap((option, optionIndex) =>
      option
        ? [
            {
              option,
              optionIndex,
            },
          ]
        : [],
    );

    if (eventOptions.length > 0) {
      return eventOptions.map(({ option, optionIndex }) => {
        const sourceTransactionId = option.sourceTransactionId?.trim();
        const uniqueSourceTransactionId =
          sourceTransactionId &&
          sourceTransactionIdCounts.get(sourceTransactionId) === 1
            ? sourceTransactionId
            : undefined;
        return {
          key: getEventOptionAttendanceKey(
            uniqueSourceTransactionId,
            guestIndex,
            optionIndex,
          ),
          guestIndex,
          optionIndex,
          optionLabel: option.label,
        };
      });
    }

    return [{ key: getGuestAttendanceKey(guestIndex), guestIndex }];
  }).flat();
}

/**
 * Returns the stable attendance keys currently marked as arrived. Legacy
 * guest indexes are replayed as normal guest keys while option keys remain
 * independent. Later history entries win.
 */
export function getArrivedAttendanceKeys(
  order: Pick<EventSessionOrder, "quantity" | "guests" | "arrivalHistory">,
  sessionId: string,
): Set<string> {
  const arrived = new Set<string>();
  const targetsByGuestIndex = new Map<number, string[]>();
  for (const target of deriveAttendanceTargets(order)) {
    targetsByGuestIndex.set(target.guestIndex, [
      ...(targetsByGuestIndex.get(target.guestIndex) ?? []),
      target.key,
    ]);
  }

  for (const entry of order.arrivalHistory ?? []) {
    if (!entry) continue;
    const entrySessionIds = (entry.sessionIds ?? []).filter(Boolean);
    if (entrySessionIds.length > 0 && !entrySessionIds.includes(sessionId)) {
      continue;
    }
    const attendanceKeys = new Set([
      ...(entry.attendanceKeys ?? []).filter(
        (key): key is string => typeof key === "string" && key.length > 0,
      ),
      ...(entry.guestIndexes ?? []).flatMap(guestIndex =>
        guestIndex == null
          ? []
          : (targetsByGuestIndex.get(guestIndex) ?? [
              getGuestAttendanceKey(guestIndex),
            ]),
      ),
    ]);
    for (const attendanceKey of attendanceKeys) {
      if (entry.action === ARRIVAL_ACTION_ARRIVED) {
        arrived.add(attendanceKey);
      } else {
        arrived.delete(attendanceKey);
      }
    }
  }

  return arrived;
}

/** Guest display label: the collected guest name, falling back to "Guest N". */
export function getGuestLabel(
  order: Pick<EventSessionOrder, "guestNames" | "guests">,
  index: number,
  t: TFunction,
): string {
  const name =
    order.guests?.[index]?.name.trim() || order.guestNames?.[index]?.trim();
  if (name) return name;
  return t("GUEST_FALLBACK", { number: index + 1 });
}

export function getAttendanceTargetLabel(
  order: Pick<EventSessionOrder, "guestNames" | "guests">,
  target: AttendanceTarget,
  t: TFunction,
): string {
  const guestLabel = getGuestLabel(order, target.guestIndex, t);
  if (target.optionIndex == null) return guestLabel;

  const optionLabel =
    target.optionLabel?.trim() ||
    t("OPTION_FALLBACK", { number: target.optionIndex + 1 });
  return `${guestLabel} — ${optionLabel}`;
}

function resolveAttendanceKeyLabel(
  order: Pick<EventSessionOrder, "quantity" | "guestNames" | "guests">,
  attendanceKey: string,
  targetsByKey: ReadonlyMap<string, AttendanceTarget>,
  t: TFunction,
): string {
  const target = targetsByKey.get(attendanceKey);
  if (target) return getAttendanceTargetLabel(order, target, t);

  const guestMatch = /^guest:(\d+)$/.exec(attendanceKey);
  if (guestMatch) {
    return getGuestLabel(order, Number(guestMatch[1]), t);
  }

  return attendanceKey;
}

/** Human-readable labels for targets affected by an arrival history entry. */
export function getEntryAttendanceLabel(
  order: Pick<EventSessionOrder, "quantity" | "guestNames" | "guests">,
  entry: Pick<EventOrderArrivalEntry, "attendanceKeys" | "guestIndexes">,
  t: TFunction,
  targetsByKey: ReadonlyMap<string, AttendanceTarget> = new Map(
    deriveAttendanceTargets(order).map(target => [target.key, target]),
  ),
): string {
  const attendanceKeys = new Set([
    ...(entry.attendanceKeys ?? []).filter(Boolean),
    ...(entry.guestIndexes ?? []).map(getGuestAttendanceKey),
  ]);

  return Array.from(attendanceKeys)
    .map(key => resolveAttendanceKeyLabel(order, key, targetsByKey, t))
    .join(", ");
}
